'use client'

import { Fragment } from 'react'

import { NestedFigsThemeProvider } from '@syconium/little-miss-figgy'
import { Plus } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/Plus'
import { MediaAsContents } from '@syconium/little-miss-figgy/dist/components/Media'
import { lgEndPixels, smEndPixels } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { PageBannerSectionFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'
import { PageSection, PageSectionContent } from '../../PageSection.client'
import { ContentfulRichText } from '../components/ContentfulRichText'
import { ContentfulStyledImage } from '../components/ContentfulStyledImage'
import { ContentfulStyledText } from '../components/ContentfulStyledText'

import {
  ContentfulPageBannerSectionAssetOverlayedContent,
  ContentfulPageBannerSectionAssetWrapper,
  ContentfulPageBannerSectionBackground,
  ContentfulPageBannerSectionContent,
  ContentfulPageBannerSectionHotspot,
  ContentfulPageBannerSectionVideoDialogButtonWrapper,
  Disclaimer,
} from './ContentfulPageBannerSection.styles'

export const pageBannerSectionFragment = gql(`
  fragment PageBannerSection on PageBannerSection {
    __typename  
    id
    name
    backgroundColorDesktop
    backgroundColorMobile
    disclaimer
    hasVerticalMargin
    hotspot {
      linkHref1
    }
    backgroundImage {
      ...StyledImage
    }
    video {
      inlineVideoDesktop
      inlineVideoMobile
      inlineVimeoIdDesktop
      inlineVimeoIdMobile
    }
    videoModal {
      modalVideoId
      modalVideoIdMobile
      openVideoModalText
    }
    isFullBleedDesktop
    overlayColorDesktop
    overlayColorMobile
    textPlacementDesktop
    textPlacementMobile
    styledContentItems {
      __typename
      ... on StyledText {
        ...StyledText
      }
      ... on StyledImage {
        ...StyledImage
      }
    }
  }
`)

export type ContentfulPageBannerSectionProps = {
  pageSectionIndex: number
  data: PageBannerSectionFragment
}

export const ContentfulPageBannerSection = ({
  data,
  pageSectionIndex,
}: ContentfulPageBannerSectionProps) => {
  const translations = useTranslation()

  const playText = translations.magnolia.general.playVideo
  const pauseText = translations.magnolia.general.pauseVideo

  const section = data
  const isFullBleedDesktop = section.isFullBleedDesktop ?? false
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasVerticalMargin = section.hasVerticalMargin ?? true
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const textPlacementDesktop = section.textPlacementDesktop ?? undefined
  // TODO: Fix this auto-generated suppression.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const textPlacementMobile = section.textPlacementMobile ?? undefined
  const backgroundColorDesktop = section.backgroundColorDesktop ?? undefined
  const backgroundColorMobile = section.backgroundColorMobile ?? undefined
  const styledContentItems = section.styledContentItems
  const lastContentItem = styledContentItems.at(-1)
  const overlayColorDesktop = section.overlayColorDesktop
  const overlayColorMobile = section.overlayColorMobile
  const hotspot = section.hotspot
  const modalVideoId = section.videoModal?.modalVideoId
  const modalVideoIdMobile = section.videoModal?.modalVideoIdMobile ?? modalVideoId
  const openVideoModalText = section.videoModal?.openVideoModalText
  const hasModalVideo = !!(modalVideoId || modalVideoIdMobile)

  const renderVideoDialogButton = ({
    location,
    forceDisplay,
  }: {
    location: 'text' | 'asset'
    forceDisplay: boolean
  }) => {
    const wrapperProps = {
      $location: location,
      $spacing: (openVideoModalText ? 'less' : 'more') as 'less' | 'more',
      $textPlacementMobile: textPlacementMobile,
      $hasLabel: !!openVideoModalText,
      $forceDisplay: forceDisplay,
      className: 'configurable-width',
    }
    return (
      <>
        {modalVideoIdMobile ? (
          <MediaAsContents lessThan='md'>
            <ContentfulPageBannerSectionVideoDialogButtonWrapper {...wrapperProps}>
              <WatchVideoDialog
                videoId={modalVideoIdMobile}
                videoPlatform='vimeo'
                text={openVideoModalText}
              />
            </ContentfulPageBannerSectionVideoDialogButtonWrapper>
          </MediaAsContents>
        ) : null}
        {modalVideoId ? (
          <MediaAsContents greaterThanOrEqual='md'>
            <ContentfulPageBannerSectionVideoDialogButtonWrapper {...wrapperProps}>
              <WatchVideoDialog
                videoId={modalVideoId}
                videoPlatform='vimeo'
                text={openVideoModalText}
              />
            </ContentfulPageBannerSectionVideoDialogButtonWrapper>
          </MediaAsContents>
        ) : null}
      </>
    )
  }
  const disclaimer = section.disclaimer

  const inlineVideoDesktop = section.video?.inlineVideoDesktop
  const inlineVideoMobile = section.video?.inlineVideoMobile
  const inlineVimeoIdDesktop = section.video?.inlineVimeoIdDesktop
  const inlineVimeoIdMobile = section.video?.inlineVimeoIdMobile

  const backgroundImage = section.backgroundImage

  const backgroundImageThemeDesktop = section.backgroundImage?.imageThemeDesktop
  const backgroundImageThemeMobile = section.backgroundImage?.imageThemeMobile

  const shouldShowHotspot =
    Boolean(hotspot?.linkHref1) &&
    (textPlacementDesktop === 'right' ||
      textPlacementDesktop === 'left' ||
      textPlacementMobile === 'below')

  const DisclaimerText = (
    <NestedFigsThemeProvider
      themeDesktop={textPlacementDesktop === 'center' ? backgroundImageThemeDesktop : 'default'}
      themeMobile={textPlacementMobile === 'below' ? 'default' : backgroundImageThemeMobile}
    >
      <Disclaimer $placement={textPlacementDesktop}>
        <ContentfulRichText content={disclaimer} bodySize='BodyTiny' fontWeight='600' />
      </Disclaimer>
    </NestedFigsThemeProvider>
  )
  return (
    <PageSection type={section.__typename} id={section.id} hasVerticalMargin={hasVerticalMargin}>
      <PageSectionContent
        gutterSize={{
          sm: 'none',
          md: isFullBleedDesktop ? 'none' : 'expanded',
        }}
      >
        <ContentfulPageBannerSectionBackground
          data-testid='page-banner-section-body'
          $backgroundColorDesktop={backgroundColorDesktop}
          $backgroundColorMobile={backgroundColorMobile}
          $textPlacementDesktop={textPlacementDesktop}
          $textPlacementMobile={textPlacementMobile}
        >
          <ContentfulPageBannerSectionAssetWrapper
            $isFullBleedDesktop={isFullBleedDesktop}
            $isTallAssetMobile={Boolean(inlineVideoMobile)}
            $overlayColorDesktop={overlayColorDesktop}
            $overlayColorMobile={overlayColorMobile}
            $textPlacementDesktop={textPlacementDesktop}
            $textPlacementMobile={textPlacementMobile}
          >
            {backgroundImage ? (
              <ContentfulStyledImage
                {...backgroundImage}
                aria-hidden='true'
                widths={{
                  unit: 'vw',
                  sm: 100,
                  md: textPlacementDesktop === 'center' ? 100 : 50,
                }}
                loading={pageSectionIndex < 2 ? 'default' : 'lazy'}
                fetchPriority={pageSectionIndex < 2 ? 'high' : 'auto'}
              />
            ) : null}

            <ClientOnlyResponsiveVideo
              playText={playText}
              pauseText={pauseText}
              inlineVideoDesktop={inlineVideoDesktop}
              inlineVideoMobile={inlineVideoMobile}
              inlineVimeoIdDesktop={inlineVimeoIdDesktop}
              inlineVimeoIdMobile={inlineVimeoIdMobile}
            />

            <NestedFigsThemeProvider
              themeDesktop={backgroundImageThemeDesktop}
              themeMobile={backgroundImageThemeMobile}
            >
              <ContentfulPageBannerSectionAssetOverlayedContent>
                {renderVideoDialogButton({
                  location: 'asset',
                  forceDisplay: styledContentItems.length === 0,
                })}
              </ContentfulPageBannerSectionAssetOverlayedContent>

              {shouldShowHotspot && hotspot?.linkHref1 ? (
                <ContentfulPageBannerSectionHotspot as={NextLink} href={hotspot.linkHref1}>
                  <Plus />
                </ContentfulPageBannerSectionHotspot>
              ) : null}
            </NestedFigsThemeProvider>
          </ContentfulPageBannerSectionAssetWrapper>

          <NestedFigsThemeProvider
            themeDesktop={
              textPlacementDesktop === 'center' ? backgroundImageThemeDesktop : 'default'
            }
            themeMobile={textPlacementMobile === 'below' ? 'default' : backgroundImageThemeMobile}
          >
            {styledContentItems.length > 0 && (
              <ContentfulPageBannerSectionContent
                $textPlacementDesktop={textPlacementDesktop}
                $textPlacementMobile={textPlacementMobile}
                $hasDisclaimer={!!disclaimer}
                $endsInLink={
                  // TODO: Fix this auto-generated suppression.
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  lastContentItem?.__typename === 'StyledText' && lastContentItem?.links.length > 0
                }
              >
                {styledContentItems.map((styledContentItem, index) => {
                  const key = `${styledContentItem.id}-${index}`
                  const isLastStyledItem = index === styledContentItems.length - 1

                  const additionalContent =
                    isLastStyledItem && hasModalVideo
                      ? renderVideoDialogButton({ location: 'text', forceDisplay: false })
                      : null

                  if (styledContentItem.__typename === 'StyledText') {
                    return (
                      <ContentfulStyledText
                        key={key}
                        {...styledContentItem}
                        centerFloatContent
                        defaultMaxWidth={450}
                        defaultBottomMargin={index < styledContentItems.length - 1 ? 24 : 0}
                        additionalCustomContent={additionalContent}
                      />
                    )
                    // TODO: Fix this auto-generated suppression.
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  } else if (styledContentItem.__typename === 'StyledImage') {
                    return (
                      <Fragment key={key}>
                        <ContentfulStyledImage
                          key={key}
                          {...styledContentItem}
                          loading='lazy'
                          transparentBackground
                          widths={
                            styledContentItem.maxWidthMobile || styledContentItem.maxWidthDesktop
                              ? {
                                  unit: 'px',
                                  sm: styledContentItem.maxWidthMobile ?? smEndPixels,
                                  md: styledContentItem.maxWidthDesktop ?? lgEndPixels,
                                }
                              : {
                                  unit: 'vw',
                                  md: textPlacementDesktop === 'center' ? 100 : 50,
                                  sm: 100,
                                }
                          }
                        />
                        {additionalContent}
                      </Fragment>
                    )
                  } else {
                    return null
                  }
                })}
                {textPlacementDesktop !== 'center' && DisclaimerText}
              </ContentfulPageBannerSectionContent>
            )}
          </NestedFigsThemeProvider>
        </ContentfulPageBannerSectionBackground>
      </PageSectionContent>
      {textPlacementDesktop === 'center' && DisclaimerText}
    </PageSection>
  )
}
